import React from 'react'
import {
  Box,
  FormControlLabel,
  IconButton,
  InputBase,
  Switch,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import SearchIcon from '@mui/icons-material/SearchRounded'
import CloseIcon from '@mui/icons-material/CloseRounded'

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect

export interface SearchToggleProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  inputRef?: React.RefObject<HTMLInputElement>
  buttonRef?: React.RefObject<HTMLButtonElement>
  sx?: SxProps<Theme>
  disabled?: boolean
  includeDismissed?: boolean
  onIncludeDismissedChange?: (next: boolean) => void
}

const SearchToggle: React.FC<SearchToggleProps> = ({
  value,
  onChange,
  placeholder,
  open,
  onOpenChange,
  inputRef,
  buttonRef,
  sx,
  disabled = false,
  includeDismissed,
  onIncludeDismissedChange,
}) => {
  const internalButtonRef = React.useRef<HTMLButtonElement | null>(null)
  const internalInputRef = React.useRef<HTMLInputElement | null>(null)
  const resolvedButtonRef = buttonRef ?? internalButtonRef
  const resolvedInputRef = inputRef ?? internalInputRef
  const manualCloseRef = React.useRef(false)
  const inputId = React.useId()

  const hasValue = value.trim().length > 0

  const containerSx = React.useMemo<SxProps<Theme>>(() => {
    const base: SxProps<Theme> = {
      display: 'inline-flex',
      alignItems: open ? 'flex-start' : 'center',
      gap: open || hasValue ? 0.5 : 0,
      borderRadius: 999,
      borderStyle: 'solid',
      borderWidth: open || hasValue ? 1 : 0,
      borderColor: (theme) =>
        open || hasValue ? alpha(theme.palette.primary.main, 0.32) : 'transparent',
      backgroundColor: (theme) => {
        if (open) {
          return theme.palette.action.hover
        }
        if (hasValue) {
          return alpha(theme.palette.primary.main, 0.12)
        }
        return 'transparent'
      },
      px: open || hasValue ? 0.75 : 0,
      py: 0.25,
      minHeight: 32,
      flex: open ? '1 1 200px' : '0 0 auto',
      minWidth: open ? 160 : 'auto',
      maxWidth: '100%',
      flexWrap: open ? 'wrap' : 'nowrap',
      transition:
        'background-color 160ms ease, border-color 160ms ease, border-width 160ms ease, padding 160ms ease, flex 160ms ease',
      opacity: disabled ? 0.55 : 1,
      pointerEvents: disabled ? 'none' : 'auto',
    }

    if (!sx) {
      return base
    }

    if (Array.isArray(sx)) {
      return [base, ...sx]
    }

    return [base, sx]
  }, [hasValue, open, sx])

  const focusInput = React.useCallback(() => {
    if (typeof window === 'undefined') {
      return
    }

    window.requestAnimationFrame(() => {
      const input = resolvedInputRef.current
      input?.focus()
      if (input) {
        const length = input.value.length
        input.setSelectionRange(length, length)
      }
    })
  }, [resolvedInputRef])

  const handleOpen = React.useCallback(() => {
    if (disabled) {
      return
    }
    manualCloseRef.current = false
    if (!open) {
      onOpenChange(true)
      return
    }
    focusInput()
  }, [disabled, focusInput, onOpenChange, open])

  const handleClose = React.useCallback(() => {
    if (disabled) {
      return
    }
    if (!open) {
      return
    }
    manualCloseRef.current = true
    onOpenChange(false)
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        resolvedButtonRef.current?.focus()
      })
    }
  }, [onOpenChange, open, resolvedButtonRef])

  useIsomorphicLayoutEffect(() => {
    if (open) {
      focusInput()
    }
  }, [focusInput, open])

  React.useEffect(() => {
    if (!open && value.trim() !== '' && !manualCloseRef.current) {
      onOpenChange(true)
    }
  }, [disabled, onOpenChange, open, value])

  React.useEffect(() => {
    if (value.trim() === '') {
      manualCloseRef.current = false
    }
  }, [value])

  React.useEffect(() => {
    if (disabled) {
      return
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) {
        return
      }
      if (event.key !== '/' || event.altKey || event.metaKey || event.ctrlKey) {
        return
      }
      const target = event.target as HTMLElement | null
      if (target) {
        const tagName = target.tagName
        if (tagName === 'INPUT' || tagName === 'TEXTAREA' || target.isContentEditable) {
          return
        }
      }
      event.preventDefault()
      if (!open) {
        manualCloseRef.current = false
        onOpenChange(true)
      } else {
        focusInput()
      }
    }

    if (typeof window === 'undefined') {
      return
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [disabled, focusInput, onOpenChange, open])

  React.useEffect(() => {
    if (disabled && open) {
      onOpenChange(false)
    }
  }, [disabled, onOpenChange, open])

  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value)
    },
    [onChange],
  )

  const handleInputKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        handleClose()
      }
    },
    [handleClose],
  )

  const showIncludeDismissedControl =
    open && typeof includeDismissed === 'boolean' && typeof onIncludeDismissedChange === 'function'

  return (
    <Box sx={containerSx} data-prep-chip="true" aria-disabled={disabled ? 'true' : undefined}>
      <IconButton
        ref={resolvedButtonRef}
        color={open || hasValue ? 'primary' : 'default'}
        aria-label="Search prep"
        aria-expanded={open ? 'true' : 'false'}
        aria-controls={open ? inputId : undefined}
        onClick={handleOpen}
        size="small"
        disabled={disabled}
        sx={{
          p: 0.5,
          minHeight: 32,
          minWidth: 32,
          transition: 'color 160ms ease',
          alignSelf: open ? 'flex-start' : 'center',
        }}
      >
        <SearchIcon fontSize="small" />
      </IconButton>
      {open ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: '1 1 160px',
            minWidth: 0,
            width: '100%',
            gap: 0.5,
          }}
        >
          <InputBase
            id={inputId}
            inputRef={resolvedInputRef}
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            placeholder={placeholder}
            inputProps={{ 'aria-label': 'Search prep' }}
            autoComplete="off"
            disabled={disabled}
            sx={{
              flex: 1,
              minWidth: 80,
              fontSize: '0.875rem',
              px: 0.5,
            }}
          />
          <IconButton
            size="small"
            aria-label="Close search"
            onClick={handleClose}
            disabled={disabled}
            sx={{
              p: 0.25,
              minHeight: 32,
              minWidth: 32,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : null}
      {showIncludeDismissedControl ? (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 0.5,
            pt: 0.25,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={Boolean(includeDismissed)}
                onChange={(event) => onIncludeDismissedChange?.(event.target.checked)}
                inputProps={{ 'aria-label': 'Include dismissed (search only)' }}
              />
            }
            label={
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                Include dismissed (search only)
              </Typography>
            }
            sx={{
              ml: 0,
              '& .MuiFormControlLabel-label': {
                color: 'text.secondary',
              },
            }}
          />
        </Box>
      ) : null}
    </Box>
  )
}

export default SearchToggle
